import type { RowData } from "@tanstack/react-table";
import { DragHandle6DotIcon } from "@icons";
import { useRowDrag } from "../features/dnd/dragContext";
import { useCellControlTabIndex } from "./cellControls";
import type { DataGridCellProps } from "./types";
import styles from "./cells.module.scss";

/**
 * Grip for `rowReorder`: drag it, or focus it and press Space to pick the row
 * up, arrows to move, Space to drop, Escape to cancel. Group rows have none.
 */
export const DragHandleCell = <TData extends RowData>({
    context,
}: Readonly<DataGridCellProps<TData>>) => {
    const drag = useRowDrag();
    const tabIndex = useCellControlTabIndex();
    const rowId = context.row.id;
    if (!drag || context.row.getIsGrouped()) {
        return null;
    }
    return (
        <div className={styles.dragHandleSlot} data-dt-controls="">
            <button
                type="button"
                className={styles.dragHandle}
                aria-label="Reorder row"
                aria-roledescription="draggable"
                aria-describedby={drag.instructionsId}
                aria-disabled={drag.isBlocked || undefined}
                data-cell-tooltip={
                    drag.isBlocked
                        ? "Clear sorting and grouping to reorder rows"
                        : undefined
                }
                data-cell-tooltip-always={drag.isBlocked ? "" : undefined}
                tabIndex={tabIndex}
                onPointerDown={(event) => drag.startRowDrag(event, rowId)}
                onKeyDown={(event) => drag.startRowDrag(event, rowId)}
            >
                <DragHandle6DotIcon aria-hidden />
            </button>
        </div>
    );
};
